import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Plus } from "lucide-react";
import { Button } from "@/shared/components/ui/button";
import { useCurrentUser } from "@/shared/auth/useAuth";
import { Role } from "@/shared/types/role";
import { CompliancePageShell } from "../shared/CompliancePageShell";
import { useDocumentTypes } from "./use-document-types";
import type { TosDocumentType } from "../shared/compliance-types";
import { CreateDocumentTypeDialog } from "./CreateDocumentTypeDialog";
import { EditDocumentTypeDialog } from "./EditDocumentTypeDialog";
import { DeleteDocumentTypeDialog } from "./DeleteDocumentTypeDialog";

export function DocumentTypesPage() {
  const { t } = useTranslation();
  const currentUser = useCurrentUser();
  const canEdit = currentUser?.role === Role.WANDERFIT_ADMIN;

  const { data: items, isLoading, isError } = useDocumentTypes();

  const [createOpen, setCreateOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<TosDocumentType | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<TosDocumentType | null>(null);

  const headerActions = canEdit ? (
    <Button data-testid="compliance-new-document-type-button" onClick={() => setCreateOpen(true)}>
      <Plus className="mr-2 h-4 w-4" aria-hidden="true" />
      {t("compliance.documentTypes.create.button")}
    </Button>
  ) : null;

  return (
    <CompliancePageShell
      screenTestId="compliance-document-types-screen"
      titleKey="compliance.documentTypes.title"
      subtitleKey="compliance.documentTypes.subtitle"
      headerActions={headerActions}
    >
      {isLoading && (
        <p
          data-testid="compliance-document-types-loading"
          className="text-sm text-muted-foreground"
        >
          {t("compliance.documentTypes.loading")}
        </p>
      )}

      {isError && (
        <p data-testid="compliance-document-types-error" className="text-sm text-destructive">
          {t("compliance.documentTypes.loadError")}
        </p>
      )}

      {items && items.length === 0 && (
        <p data-testid="compliance-document-types-empty" className="text-sm text-muted-foreground">
          {t("compliance.documentTypes.empty")}
        </p>
      )}

      {items && items.length > 0 && (
        <ul className="space-y-2">
          {items.map((dt) => (
            <li
              key={dt.id}
              data-testid={`compliance-document-type-row-${dt.key}`}
              className="flex flex-wrap items-start justify-between gap-3 rounded-md border border-border bg-card p-4"
            >
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <code className="rounded bg-muted px-1.5 py-0.5 font-mono text-xs">{dt.key}</code>
                  {dt.isSystem && (
                    <span className="rounded-full bg-wander-gray/15 px-2 py-0.5 text-xs text-wander-gray ring-1 ring-inset ring-wander-gray/40">
                      {t("compliance.documentTypes.systemBadge")}
                    </span>
                  )}
                </div>
                <p className="font-medium">{dt.name}</p>
                {dt.description && (
                  <p className="text-sm text-muted-foreground">{dt.description}</p>
                )}
              </div>

              {canEdit && (
                <div className="flex shrink-0 gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    data-testid={`compliance-edit-document-type-${dt.key}`}
                    onClick={() => setEditTarget(dt)}
                  >
                    {t("compliance.actions.edit")}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={dt.isSystem}
                    title={dt.isSystem ? t("compliance.documentTypes.systemTooltip") : undefined}
                    data-testid={`compliance-delete-document-type-${dt.key}`}
                    onClick={() => setDeleteTarget(dt)}
                  >
                    {t("compliance.actions.delete")}
                  </Button>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}

      <CreateDocumentTypeDialog open={createOpen} onOpenChange={setCreateOpen} />
      <EditDocumentTypeDialog
        target={editTarget}
        onOpenChange={(open) => !open && setEditTarget(null)}
      />
      <DeleteDocumentTypeDialog
        target={deleteTarget}
        onOpenChange={(open) => !open && setDeleteTarget(null)}
      />
    </CompliancePageShell>
  );
}
